import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AsButtonDirective } from '@directives/as-button/as-button.directive';
import { StopClickPropagationDirective } from '@directives/stop-click-propagation/stop-click-propagation.directive';
import { ActivityStep } from '@models/activity.model';
import { LinePlan } from '@models/asset-details.model';
import { UserAsset } from '@models/dashboard.model';
import {
  calculateItemsToDelete,
  createShoppingObject,
  createShoppingPriceObject,
  stringOnlyHTML
} from '@models/functions.model';
import { CartInfo } from '@models/session-details';
import { InCartObjects, ShoppingCartData, ShoppingCartItem } from '@models/shopping-cart.model';
import {
  ADMIN_OPTIONS,
  CART_ITEM_TYPE_ACCESSORY,
  CART_ITEM_TYPE_DEVICE,
  CART_ITEM_TYPE_FEATURE,
  CART_ITEM_TYPE_PLAN,
  DeviceData,
  DeviceItemData,
  DeviceItemDevice,
  DeviceItemDeviceBP,
  DevicePrice,
  FeatureType,
  PlanType,
  ShoppingDataInitialLoad,
  ShoppingFilterItem,
  StorageListResponse,
  StoreQueryParams
} from '@models/shopping.model';
import { NgbActiveModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { ModalService } from '@services/modal/modal.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { BaseBadgeComponent } from '@shared-components/badge/base-badge/base-badge.component';
import { PriceSummaryComponent } from '@shared-components/price-summary/price-summary.component';
import { ViewDetailsComponent } from '@shared-components/view-details/view-details.component';
import { Subject, Subscription, asapScheduler, takeUntil } from 'rxjs';
import {
  DataLoaderCaptureOverrideDirective,
  DataLoaderDirective,
  DataLoaderService
} from 'tangoe-design-system';

import { AdminOverrideBadgeComponent } from '../admin-override/admin-override-badge/admin-override-badge.component';
import { AdminOverrideBannerComponent } from '../admin-override/admin-override-banner/admin-override-banner.component';
import { CarrierComponent } from '../customize-device/carrier/carrier.component';
import { DeviceDescriptionComponent } from '../customize-device/device-description/device-description.component';
import { ImagePlaceholderComponent } from '../customize-device/image-placeholder/image-placeholder.component';

@Component({
  selector: 'ts-device-details',
  templateUrl: './device-details.component.html',
  providers: [NgbActiveModal],
  hostDirectives: [DataLoaderCaptureOverrideDirective],
  imports: [
    CommonModule,
    TranslationPipe,
    AsButtonDirective,
    DataLoaderDirective,
    StopClickPropagationDirective,
    AdminOverrideBadgeComponent,
    AdminOverrideBannerComponent,
    BaseBadgeComponent,
    CarrierComponent,
    DeviceDescriptionComponent,
    ImagePlaceholderComponent,
    PriceSummaryComponent
  ]
})
export class DeviceDetailsComponent implements OnInit, OnDestroy {
  current_activity_id: number;
  selected_device_manufacturer: string;
  selected_device_model: string;
  selected_device_name: string | null;
  selected_device_id: number;
  selected_devices_by_carrier: DeviceItemData;
  selected_carrier_id: number;
  selected_normalized_color: string;
  selected_device_size: string;
  selected_storage_size: string;
  selected_name_override_id: number = -1;
  selected_plan_id: number | undefined;
  selected_feature_list: FeatureType[] = [];
  selected_storage_device_price: DevicePrice = {
    onetime: { cost_to_currency: '' },
    monthly: { cost_to_currency: '' }
  };
  selected_storage_device_id: number = -1;
  selected_color_device_id: number = -1;
  device_selection_device_id: number | undefined = undefined;
  device_data: DeviceData;
  device_record: DeviceItemData[];
  is_bp_device: boolean | undefined;
  normalized_device_list_by_color: DeviceItemDevice[];
  active_device: DeviceItemDevice | DeviceItemDeviceBP;
  storage_list: DeviceItemDevice[] = [];
  name_override_list: DeviceItemDevice[] = [];
  plan_list: PlanType[] = [];
  feature_list: FeatureType[] = [];
  no_plans: boolean = false;
  no_features: boolean = false;
  keep_plan: boolean = false;
  is_display_one_time_cost: boolean = false;
  is_display_recurring_cost: boolean = false;
  existing_image_url: string | undefined;
  show_options: boolean = false;
  accessories_enabled: boolean = true;
  url_filters_param: string;
  devices_payload: any = {
    filters: [],
    orderTypeId: 0,
    categories: [],
    countryId: 0,
    include_admin_options: undefined,
    isDeviceItemRequest: true
  };
  device_plan: ShoppingDataInitialLoad = {
    categories: [],
    filters: [],
    orderTypeId: 0,
    deviceId: null
  };
  shopping_cart: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0,
    lineId: undefined
  };
  shopping_items_price: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0,
    lineId: undefined
  };
  in_cart_device: DeviceItemDevice | null;
  revisiting: boolean = false;
  admin_override_persist: boolean = false;
  modal_dismissed: boolean = false;
  progress_bar_config: ActivityStep[];
  refresh_summary: Subject<boolean | string> = new Subject<boolean | string>();
  plan_features_sub: Subject<void> = new Subject<void>();
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activated_route: ActivatedRoute,
    private activity_state_svc: ActivityStateService,
    private data_loader_svc: DataLoaderService,
    private modal_svc: ModalService,
    private router: Router,
    private session_svc: SessionService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.current_activity_id =
      this.device_plan.orderTypeId =
      this.devices_payload.orderTypeId =
      this.shopping_cart.orderTypeId =
      this.shopping_items_price.orderTypeId =
        this.activity_state_svc.orderTypeId()!;

    this.shopping_cart.lineId = this.shopping_items_price.lineId =
      this.activity_state_svc.parameter('lineId');

    this.devices_payload.include_admin_options = this.shopping_cart.device_include_admin_options =
      this.activity_state_svc.parameter('device_include_admin_options');

    this.subscriptions.add(
      this.activated_route.queryParamMap.subscribe({
        next: (paramMap) => {
          this.selected_carrier_id = Number(paramMap.get('carrierId'));
          this.selected_device_id = Number(paramMap.get('deviceId')); // not for all activity types
          this.selected_device_name = paramMap.get('name');
          this.devices_payload.keep_plan = this.keep_plan = paramMap.get('keep_plan') === 'true';
          this.devices_payload.categories = JSON.parse(paramMap.get('categories') as string);
          this.devices_payload.countryId = this.activity_state_svc.parameter('country')?.id;
          this.is_bp_device = paramMap.get('isBpDevice') === 'true';

          if ([3].includes(this.current_activity_id) || this.is_bp_device) {
            this.url_filters_param = paramMap.get('filters') as string;
            this.devices_payload.filters = JSON.parse(paramMap.get('filters') as string);
            this.selected_device_manufacturer = this.devices_payload.filters.filter(
              (filter: ShoppingFilterItem) => filter.filter_name === 'manufacturer'
            )[0].value;
            this.selected_device_model = this.devices_payload.filters.filter(
              (filter: ShoppingFilterItem) => filter.filter_name === 'model'
            )[0].value;
            this.selected_device_size = this.devices_payload.filters.filter(
              (filter: ShoppingFilterItem) => filter.filter_name === 'size'
            )[0]?.value;
            this.selected_normalized_color = this.devices_payload.filters.filter(
              (filter: ShoppingFilterItem) => filter.filter_name === 'color'
            )[0].value;
          } else {
            const filter: ShoppingFilterItem = {
              id: Number(paramMap.get('filterConfigId')),
              value: this.selected_device_name!
            };
            if (
              this.devices_payload.filters.every(
                (f: ShoppingFilterItem) => f.id !== filter.id && f.value !== filter.value
              )
            )
              this.devices_payload.filters.push(filter);
          }
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
    this.loadDevice();
    this.data_loader_svc.toggle(false);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.plan_features_sub.unsubscribe();
    this.shopping_svc.preselected_item = false;
  }

  get page_to_navigate(): string {
    return this.accessories_enabled
      ? `shop/${this.activity_state_svc.orderTypeId()}/accessories`
      : `shop/${this.activity_state_svc.orderTypeId()}/finalize-order`;
  }

  get content_loading(): boolean {
    return (
      this.data_loader_svc.isLoading('device-plans') ||
      this.data_loader_svc.isLoading('device-plan-features')
    );
  }

  get is_admin(): boolean {
    return this.session_svc.getUserProfile()?.admin ?? false;
  }

  get disable_admin_overide(): boolean {
    return this.device_data?.admin_options === ADMIN_OPTIONS.ALL_ADMIN_OPTIONS;
  }

  get admin_cart_items(): boolean {
    return CartInfo.cart_data?.shopping_cart_items.some(
      (item: ShoppingCartItem) =>
        [CART_ITEM_TYPE_DEVICE, CART_ITEM_TYPE_PLAN, CART_ITEM_TYPE_FEATURE].includes(item.type) &&
        item.additional_info?.admin
    )!;
  }

  get disable_continue_btn(): boolean {
    const cart_params: string[] = [
      CART_ITEM_TYPE_PLAN,
      ...(this.shopping_cart.orderTypeId !== 3 ? [CART_ITEM_TYPE_DEVICE] : [])
    ];

    let valid_cart: boolean =
      this.shopping_cart.shopping_cart_items.filter((item: ShoppingCartItem) =>
        cart_params.includes(item.type)
      ).length === cart_params.length;

    if (this.show_options) valid_cart = valid_cart && this.selected_name_override_id !== -1;

    if (this.keep_plan) return !(valid_cart && this.keep_plan && Boolean(this.plan_list.length));

    return !valid_cart;
  }

  sanitizedHTML = (description: string | undefined): boolean =>
    !stringOnlyHTML(description) && description !== '';

  appendMonthlyLabel = (cost: string): string => (cost.match(/mo(nth)?$/i) ? cost : `${cost}/mo`);

  getItemByType = (type: string): ShoppingCartItem | undefined =>
    this.shopping_cart.shopping_cart_items.find((item: ShoppingCartItem) => item.type === type);

  getItemById = (id: string | number): ShoppingCartItem | undefined =>
    this.shopping_cart.shopping_cart_items.find((item: ShoppingCartItem) => item.id == id);

  getActiveStorage = (device: DeviceItemDevice): boolean =>
    device.size === this.selected_storage_size &&
    device.price.onetime.cost_to_currency ===
      this.selected_storage_device_price.onetime.cost_to_currency &&
    (device.price.monthly
      ? device.price.monthly?.cost_to_currency ===
        this.selected_storage_device_price.monthly.cost_to_currency
      : true);

  getActiveFeatures(feature_details: FeatureType): boolean {
    if ((this.selected_plan_id && feature_details.included) || this.keep_plan) return true;
    else
      return !!this.selected_feature_list?.find(
        (feature: FeatureType) => feature.id == feature_details.id
      );
  }

  confirmAdminToggleClearCart(admin_override: boolean): void {
    this.subscriptions.add(
      this.shopping_svc.getShoppingItems().subscribe({
        next: (res: ShoppingCartData) => {
          let cart_items_to_delete: ShoppingCartItem[] = res.shopping_cart_items.filter(
            (cart_item: ShoppingCartItem) => cart_item.type !== CART_ITEM_TYPE_ACCESSORY
          );
          this.confirmChangesModal(
            () => this.getAdminOverride(admin_override, true),
            cart_items_to_delete
          );
        }
      })
    );
  }

  getAdminOverride(admin_override: boolean, clear_cart: boolean = false): void {
    let cart_updated: boolean = false;
    if (!this.disable_admin_overide) {
      this.devices_payload.include_admin_options = this.shopping_cart.device_include_admin_options =
        admin_override;

      if (
        !admin_override &&
        (this.getItemByType(CART_ITEM_TYPE_DEVICE)?.additional_info?.admin || clear_cart)
      )
        this.resetPlansAndFeatures();
      else if (!admin_override) {
        this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.reduce(
          (prv: ShoppingCartItem[], curr: ShoppingCartItem) => {
            if (curr.type === CART_ITEM_TYPE_PLAN && curr.additional_info?.admin) {
              this.removeItems(CART_ITEM_TYPE_FEATURE);
              this.selected_plan_id = undefined;
              this.feature_list = [];
              cart_updated = true;
              return prv;
            } else if (
              curr.type === CART_ITEM_TYPE_FEATURE &&
              (curr.additional_info?.admin || curr.additional_info?.included)
            ) {
              cart_updated = true;
              return prv;
            }
            return [...prv, curr];
          },
          []
        );
        this.shopping_items_price.shopping_cart_items =
          this.shopping_items_price.shopping_cart_items.filter((item: ShoppingCartItem) =>
            this.getItemById(item.id)
          );
      } else {
        cart_updated = false;
      }

      if (
        this.shopping_cart?.shopping_cart_items.find(
          (item: ShoppingCartItem) =>
            item.type === CART_ITEM_TYPE_PLAN || item.type === CART_ITEM_TYPE_FEATURE
        )
      )
        this.admin_override_persist = true;

      this.loadDevice(() => {
        this.getShoppingCartData(this.shopping_cart, true);
        if (cart_updated) this.refresh_summary.next(true);
      });
    }
  }

  selectCarrierClick(device_item: DeviceItemData): void {
    if (
      this.revisiting &&
      device_item.carrier.id != this.selected_carrier_id &&
      !this.modal_dismissed
    ) {
      this.confirmChangesModal(() => this.selectCarrier(device_item));
    } else this.selectCarrier(device_item);
  }

  selectColorClick(device_details: DeviceItemDevice): void {
    if (
      this.revisiting &&
      device_details.id != this.selected_color_device_id &&
      !this.modal_dismissed
    ) {
      this.confirmChangesModal(() => this.selectColor(device_details));
    } else this.selectColor(device_details);
  }

  selectStorageClick(device_details: DeviceItemDevice): void {
    if (
      this.revisiting &&
      (device_details.size != this.selected_storage_size ||
        device_details.id != this.selected_device_id) &&
      this.selected_storage_size !== '' &&
      !this.modal_dismissed
    ) {
      this.confirmChangesModal(() => this.selectStorage(device_details));
    } else this.selectStorage(device_details);
  }

  selectNameOverrideClick(device_details: DeviceItemDevice): void {
    if (
      this.revisiting &&
      device_details.id !== this.selected_name_override_id &&
      this.selected_name_override_id !== -1 &&
      !this.modal_dismissed
    ) {
      this.confirmChangesModal(() => this.selectNameOverride(device_details));
    } else this.selectNameOverride(device_details);
  }

  selectPlan(
    plan_details: PlanType,
    restore_feature_ids?: Array<string | number>,
    force_reload?: boolean
  ): void {
    if (this.selected_plan_id == plan_details.id && !this.keep_plan && !force_reload) return;

    const feature_ids_to_restore: Array<string | number> =
      restore_feature_ids ??
      (this.selected_feature_list.length
        ? this.selected_feature_list.map((f: FeatureType) => f.id)
        : this.shopping_cart.shopping_cart_items
            .filter((item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE)
            .map((item: ShoppingCartItem) => item.id));

    this.removeItems(CART_ITEM_TYPE_PLAN);
    if (!this.keep_plan) this.removeItems(CART_ITEM_TYPE_FEATURE);
    this.plan_features_sub.next();

    this.is_display_recurring_cost = true;
    this.selected_plan_id = plan_details.id;
    if (!this.keep_plan) this.loadFeatureList(plan_details.id, feature_ids_to_restore);
    this.generateShoppingItems(plan_details, CART_ITEM_TYPE_PLAN, true);
  }

  selectFeature(feature_details: FeatureType): void {
    const selected_features: boolean = !!this.selected_feature_list.find(
      (feature: FeatureType) => feature.id == feature_details.id
    );
    const feature_in_cart: boolean = !!this.getItemById(feature_details.id);

    if (selected_features) {
      this.selected_feature_list = this.selected_feature_list.filter(
        (feature: FeatureType) => feature.id != feature_details.id
      );
    } else this.selected_feature_list.push(feature_details);

    if (!feature_in_cart) this.generateShoppingItems(feature_details, CART_ITEM_TYPE_FEATURE, true);
    else if (!feature_details.included && !this.keep_plan) {
      this.shopping_svc.clearSpecifiedCartItems([feature_details.id], false).subscribe();
      this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.filter(
        (item: ShoppingCartItem) => item.id != feature_details.id
      );
      this.shopping_items_price.shopping_cart_items =
        this.shopping_items_price.shopping_cart_items.filter(
          (item: ShoppingCartItem) => item.id != feature_details.id
        );
      this.refresh_summary.next(true);
    }
  }

  getShoppingCartData(shopping_cart: ShoppingCartData, device_refreshed: boolean = false): void {
    if (shopping_cart) {
      this.setCartValues(shopping_cart);

      if (!device_refreshed) {
        this.revisiting = ![3].includes(this.current_activity_id)
          ? shopping_cart.shopping_cart_items.filter(
              (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_DEVICE
            ).length > 0
          : shopping_cart.shopping_cart_items.filter(
              (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_PLAN
            ).length > 0;

        this.selected_device_id =
          shopping_cart.existing_asset?.device?.id ?? this.selected_device_id;
      }

      if ([2].includes(this.current_activity_id)) this.displayOldPlanAndFeatures(shopping_cart);
      else
        shopping_cart.shopping_cart_items.forEach((item: ShoppingCartItem) => {
          if (!this.getItemById(item.id))
            this.generateShoppingItems(item, item.type, false, item.additional_info?.category_id);
        });

      const cart_objects: InCartObjects = this.retrieveCartObjects(
        undefined,
        [3].includes(this.current_activity_id) && shopping_cart?.existing_asset
          ? shopping_cart.existing_asset
          : undefined
      );

      if (cart_objects.device_id || cart_objects.plan_id) {
        if (this.in_cart_device) {
          if ([1001].includes(this.activity_state_svc.orderTypeId())) {
            this.shopping_svc
              .getStorageList(
                {
                  ...this.devices_payload,
                  business_purpose: this.is_bp_device
                    ? (this.active_device as DeviceItemDeviceBP).business_purpose
                    : undefined
                },
                this.selected_carrier_id,
                this.selected_normalized_color
              )
              .subscribe({
                next: (res: StorageListResponse) => {
                  this.storage_list = res.items;

                  if (this.revisiting)
                    shopping_cart.shopping_cart_items.find(
                      (cart_item: ShoppingCartItem, index: number = 0) => {
                        if (cart_item.type === CART_ITEM_TYPE_DEVICE && cart_item.additional_info) {
                          cart_item.additional_info.business_purpose = this.is_bp_device
                            ? (this.active_device as DeviceItemDeviceBP).business_purpose
                            : undefined;
                          shopping_cart.shopping_cart_items[index] = cart_item;
                        }

                        index++;
                      }
                    );

                  this.shopping_cart = shopping_cart;
                  this.loadStorageListPostCheck();
                },
                error: (e: HttpErrorResponse) => {}
              });
          } else {
            this.selected_storage_device_id = this.storage_list.find((device: DeviceItemDevice) => {
              return (
                (device.size === this.in_cart_device?.size &&
                  device.price.onetime.cost_to_currency ===
                    this.in_cart_device?.price.onetime.cost_to_currency &&
                  (this.in_cart_device?.price.monthly
                    ? this.in_cart_device.price.monthly.cost_to_currency ===
                      device.price.monthly?.cost_to_currency
                    : true)) ||
                device.id === this.in_cart_device?.id
              );
            })!.id;
            this.selected_device_id = this.selected_name_override_id = this.in_cart_device.id;
          }
        } else if (cart_objects.device_id)
          this.selected_storage_device_id = this.selected_device_id = cart_objects.device_id;

        if (![3].includes(this.current_activity_id)) {
          if (
            this.selected_name_override_id !== -1 ||
            this.selected_storage_device_id !== -1 ||
            this.in_cart_device
          )
            this.selectStorage(
              ([...this.name_override_list, ...this.storage_list].find((device: DeviceItemDevice) =>
                [this.selected_name_override_id, this.selected_storage_device_id].includes(
                  device.id
                )
              ) as DeviceItemDevice) ?? this.in_cart_device
            );
        } else {
          this.selected_storage_size = this.selected_device_size;
          if (shopping_cart.shopping_cart_items.length > 0)
            this.selected_name_override_id = shopping_cart.existing_asset?.device.id as number;
          this.selected_plan_id = cart_objects.plan_id;
        }

        if (
          !this.keep_plan &&
          (!!this.name_override_list.find(
            (device: DeviceItemDevice) => device.id == this.selected_name_override_id
          ) ||
            this.name_override_list.length === 1)
        ) {
          let cart_name_override: DeviceItemDevice | undefined = this.name_override_list.find(
            (device: DeviceItemDevice) => device.id == this.selected_name_override_id
          );

          this.selectNameOverride(cart_name_override ?? this.name_override_list[0], true);
        }
      }

      this.is_display_one_time_cost =
        this.keep_plan ||
        this.revisiting ||
        this.selected_storage_device_id !== -1 ||
        this.shopping_cart.shopping_cart_items.find(
          (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_ACCESSORY
        )
          ? true
          : false;

      this.is_display_recurring_cost =
        this.keep_plan || this.revisiting || this.selected_plan_id ? true : false;
    }
  }

  openDetailsModal(obj: PlanType | FeatureType, body: string): void {
    let active_element: HTMLElement = document.activeElement as HTMLElement;
    active_element.blur();

    const modalRef: NgbModalRef = this.modal_svc.open(ViewDetailsComponent, {
      backdrop: 'static',
      centered: true,
      scrollable: true,
      size: 'lg',
      modalDialogClass: 'scrollable-modal-maxheight'
    });
    modalRef.componentInstance.modal_title = obj.name_override ? obj.name_override : obj.name;
    modalRef.componentInstance.modal_body = body;
  }

  private loadDevice(callback?: Function): void {
    this.subscriptions.add(
      this.shopping_svc.getDevices(this.devices_payload, 'global').subscribe({
        next: (res: DeviceData) => {
          if (res.items.length) {
            this.device_data = res;
            this.accessories_enabled = res.progress_bar_config.is_accessory_ordering_enabled;
            this.progress_bar_config = res.progress_bar_config.steps;
            this.device_record = this.is_bp_device
              ? this.setBpDeviceRecord(res.items as DeviceItemData[])
              : (res.items as DeviceItemData[]);
            this.in_cart_device = res.in_cart_device;
            this.assignData();

            if (this.device_record && callback) asapScheduler.schedule(() => callback());
          }
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  private setBpDeviceRecord(items: DeviceItemData[]): DeviceItemData[] {
    let bp_device_record: DeviceItemData[] = {} as DeviceItemData[];
    items.forEach((item: DeviceItemData) => {
      item.devices.forEach((device: DeviceItemDevice) => {
        if (device.id === this.selected_device_id) {
          item.devices = [device];
          bp_device_record = [item];
          return;
        }
      });
    });
    return bp_device_record;
  }

  private assignData(device_record?: DeviceItemData): void {
    this.selected_devices_by_carrier = !device_record
      ? (this.device_record.find(
          (item: DeviceItemData) => item.carrier.id === Number(this.selected_carrier_id)
        ) as DeviceItemData) || this.device_record[0]
      : device_record;

    this.selected_carrier_id = this.selected_devices_by_carrier.carrier.id;
    this.normalized_device_list_by_color = this.getUniqColorWithOrder(
      this.selected_devices_by_carrier.devices
    );

    if ([3].includes(this.current_activity_id)) {
      let temp_device_record: DeviceItemData[] = this.device_record.filter(
        (record: DeviceItemData) => {
          record.devices = record.devices.filter((device: DeviceItemDevice) => {
            if (this.selected_device_size) {
              if (this.selected_normalized_color) {
                return (
                  device.manufacturer === this.selected_device_manufacturer &&
                  device.model === this.selected_device_model &&
                  device.color === this.selected_normalized_color &&
                  device.size === this.selected_device_size
                );
              } else {
                return (
                  device.manufacturer === this.selected_device_manufacturer &&
                  device.model === this.selected_device_model &&
                  device.size === this.selected_device_size
                );
              }
            } else {
              if (this.selected_normalized_color) {
                return (
                  device.manufacturer === this.selected_device_manufacturer &&
                  device.model === this.selected_device_model &&
                  device.color === this.selected_normalized_color
                );
              } else {
                return (
                  device.manufacturer === this.selected_device_manufacturer &&
                  device.model === this.selected_device_model
                );
              }
            }
          });
          record.total = record.devices.length;

          return record.carrier.id === this.selected_carrier_id;
        }
      );
      this.active_device = temp_device_record[0].devices[0];
      this.selectStorage(this.active_device);
    } else {
      this.active_device = this.is_bp_device
        ? (this.device_record[0].devices[0] as DeviceItemDeviceBP)
        : this.selected_devices_by_carrier.devices.find(
            (device: DeviceItemDevice) => device.id == this.selected_device_id
          ) || this.normalized_device_list_by_color[0];
      this.selected_normalized_color = this.active_device.color;
    }

    this.selected_device_id = this.active_device.id;
    this.loadStorageList(this.active_device || this.normalized_device_list_by_color[0]);
    this.updateQueryParams();
  }

  private getUniqColorWithOrder(device_list: DeviceItemDevice[]): DeviceItemDevice[] {
    let sorted_color_list: DeviceItemDevice[] = this.shopping_svc.sortColorByName(
      this.shopping_svc.uniqColorByName(device_list)
    );

    if (this.in_cart_device)
      this.selected_color_device_id = this.selected_device_id =
        sorted_color_list.find(
          (device: DeviceItemDevice) => device.color === this.in_cart_device?.color
        )?.id ?? this.storage_list[0].id;
    else
      this.selected_color_device_id = this.selected_device_id =
        (sorted_color_list.find(
          (device: DeviceItemDevice) =>
            device.original_color ===
            this.getItemByType(CART_ITEM_TYPE_DEVICE)?.additional_info?.color
        )?.id as number) ?? sorted_color_list[0].id;

    return sorted_color_list;
  }

  private loadStorageList(device_details: DeviceItemDevice): void {
    if ([1001].includes(this.activity_state_svc.orderTypeId())) {
      this.shopping_svc
        .getStorageList(
          {
            ...this.devices_payload,
            business_purpose: this.is_bp_device
              ? (this.active_device as DeviceItemDeviceBP).business_purpose
              : undefined
          },
          this.selected_carrier_id,
          this.selected_normalized_color
        )
        .subscribe({
          next: (res: StorageListResponse) => {
            this.storage_list = res.items;
            this.loadStorageListPostCheck();
          },
          error: (e: HttpErrorResponse) => {}
        });
    } else {
      const storage_sizes: string[] = ['MB', 'GB', 'TB'];
      let final_storage_list: DeviceItemDevice[] = [];

      let initial_storage_list: DeviceItemDevice[] = this.selected_devices_by_carrier.devices
        .filter(
          (device: DeviceItemDevice) =>
            device.color.toLowerCase() === device_details.color.toLowerCase()
        )
        .sort(
          (a: DeviceItemDevice, b: DeviceItemDevice) =>
            Number(a.size.slice(0, -2)) - Number(b.size.slice(0, -2))
        )
        .sort(
          (a: DeviceItemDevice, b: DeviceItemDevice) =>
            storage_sizes.indexOf(a.size.slice(a.size.length - 2)) -
            storage_sizes.indexOf(b.size.slice(b.size.length - 2))
        );

      initial_storage_list.forEach((i: DeviceItemDevice) => {
        let count: number = i.price.monthly
          ? final_storage_list.filter(
              (f: DeviceItemDevice) =>
                f.size === i.size &&
                f.price.onetime.cost_to_currency === i.price.onetime.cost_to_currency &&
                f.price.monthly.cost_to_currency === i.price.monthly.cost_to_currency
            ).length
          : final_storage_list.filter(
              (f: DeviceItemDevice) =>
                f.size === i.size &&
                f.price.onetime.cost_to_currency === i.price.onetime.cost_to_currency
            ).length;

        if (count === 0) final_storage_list.push(i);
      });
      this.storage_list = final_storage_list;

      this.loadStorageListPostCheck();
    }
  }

  private loadStorageListPostCheck(): void {
    if (this.storage_list.length === 1 && !this.getItemByType(CART_ITEM_TYPE_DEVICE)) {
      this.shopping_svc.preselected_item = true;
      this.selectStorage(this.in_cart_device || this.storage_list[0]);
    }
  }

  private loadNameOverrides(
    device_details: DeviceItemDevice,
    allow_plan_load: boolean = false
  ): void {
    if ([1001].includes(this.activity_state_svc.orderTypeId())) {
      this.shopping_svc
        .getNameOverridesList(
          {
            ...this.devices_payload,
            business_purpose: this.is_bp_device
              ? (this.active_device as DeviceItemDeviceBP).business_purpose
              : undefined,
            onetime_price_in_cents: device_details.price.onetime.fractional
          },
          this.selected_carrier_id,
          this.selected_normalized_color,
          this.selected_storage_size
        )
        .subscribe({
          next: (res: StorageListResponse) => {
            this.name_override_list = res.items;

            this.name_override_list.forEach((override: DeviceItemDevice) => {
              if (override.id === device_details.id && this.revisiting)
                this.selected_name_override_id = device_details.id;
            });

            this.loadNameOverridePostCheck(device_details, allow_plan_load);
          },
          error: (e: HttpErrorResponse) => {}
        });
    } else {
      this.name_override_list = device_details.price.monthly
        ? this.selected_devices_by_carrier.devices.filter(
            (device: DeviceItemDevice) =>
              device.carrier_id === this.selected_carrier_id &&
              device.color === this.selected_normalized_color &&
              device.size === this.selected_storage_size &&
              device.price.onetime.cost_to_currency ===
                this.selected_storage_device_price.onetime.cost_to_currency &&
              device.price.monthly.cost_to_currency ===
                this.selected_storage_device_price.monthly.cost_to_currency
          )
        : this.selected_devices_by_carrier.devices.filter(
            (device: DeviceItemDevice) =>
              device.carrier_id === this.selected_carrier_id &&
              device.color === this.selected_normalized_color &&
              device.size === this.selected_storage_size &&
              device.price.onetime.cost_to_currency ===
                this.selected_storage_device_price.onetime.cost_to_currency
          );

      this.loadNameOverridePostCheck(device_details, allow_plan_load);
    }
  }

  private loadNameOverridePostCheck(
    device_details: DeviceItemDevice,
    allow_plan_load: boolean = false
  ): void {
    this.show_options =
      this.name_override_list.length > 1 ||
      (this.name_override_list.length > 0 &&
        this.name_override_list.every((device: DeviceItemDevice) => device.name_override));

    if (this.show_options) {
      this.name_override_list.map((device: DeviceItemDevice) => {
        if (!device.name_override) device.name_override = device.name;
      });
    }

    // Currently unused, kept to prevent potential issues in future changes.
    const nonAccessoryCartItems: ShoppingCartItem[] = [
      ...CartInfo.cart_data?.shopping_cart_items!,
      ...this.shopping_cart.shopping_cart_items
    ].filter((item: ShoppingCartItem) => item.type !== CART_ITEM_TYPE_ACCESSORY);

    const has_selected_override: boolean = this.selected_name_override_id !== -1;
    const selected_override: DeviceItemDevice | undefined = has_selected_override
      ? this.name_override_list.find(
          (device: DeviceItemDevice) => device.id === this.selected_name_override_id
        )
      : undefined;

    if (
      this.name_override_list.length === 0 ||
      (this.name_override_list.length > 1 && this.selected_name_override_id === -1)
    ) {
      if (allow_plan_load && !this.keep_plan && this.name_override_list.length === 0)
        this.loadDevicePlanData(device_details.id);

      if (
        !this.shopping_cart.shopping_cart_items.find(
          (cart_item: ShoppingCartItem) => cart_item.type === CART_ITEM_TYPE_DEVICE
        )
      )
        this.generateShoppingItems(
          device_details,
          CART_ITEM_TYPE_DEVICE,
          true,
          this.devices_payload.categories[0].id
        );
    } else if (this.name_override_list.length === 1)
      this.selectNameOverride(this.name_override_list[0], true);
    else if (selected_override && allow_plan_load) this.selectNameOverride(selected_override, true);
  }

  private loadDevicePlanData(
    device_id: number,
    callback?: Function,
    allow_auto_select_plan: boolean = true
  ): void {
    const param: { [key: string]: number | string | boolean } = {
      deviceId: device_id
    };
    this.device_plan.deviceId = device_id;
    this.plan_list = [];

    if (this.shopping_cart?.device_include_admin_options !== undefined)
      param['include_admin_options'] = this.shopping_cart.device_include_admin_options;

    this.shopping_svc
      .getPlans(this.selected_carrier_id, param)
      .pipe(takeUntil(this.plan_features_sub))
      .subscribe({
        next: (res: { plans: PlanType[] }) => {
          this.plan_list = res.plans;
          this.no_plans = !res.plans.length;

          let cart_plan: ShoppingCartItem | undefined = this.getItemByType(CART_ITEM_TYPE_PLAN);
          let cart_plan_in_list: PlanType | undefined = this.plan_list.find(
            (plan: PlanType) => plan.id == cart_plan?.id
          );

          const feature_ids_to_restore: Array<string | number> =
            this.shopping_cart.shopping_cart_items
              .filter((item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE)
              .map((item: ShoppingCartItem) => item.id) ?? [];

          if (!cart_plan_in_list && this.plan_list.length === 1 && allow_auto_select_plan)
            this.selectPlan(this.plan_list[0], feature_ids_to_restore, this.revisiting);
          else if (cart_plan) {
            if (cart_plan_in_list && !callback && allow_auto_select_plan)
              this.selectPlan(cart_plan_in_list, feature_ids_to_restore, this.revisiting);
            else if (cart_plan_in_list && callback) asapScheduler.schedule(() => callback());
            else {
              this.removeItems(CART_ITEM_TYPE_PLAN);
              this.removeItems(CART_ITEM_TYPE_FEATURE);
              this.refresh_summary.next(true);
            }
          }

          this.createPayloadString();
        },
        error: (err: HttpErrorResponse) => {}
      });
  }

  private loadFeatureList(plan_id: number, restore_feature_ids?: Array<string | number>): void {
    let cart_updated: boolean = false;
    const param: { [key: string]: number | string | boolean } = {
      deviceId: this.device_plan.deviceId!,
      planId: plan_id
    };
    this.feature_list = this.selected_feature_list = [];

    if (this.shopping_cart?.device_include_admin_options !== undefined)
      param['include_admin_options'] = this.shopping_cart.device_include_admin_options;

    this.shopping_svc
      .getFeatures(this.selected_carrier_id, param)
      .pipe(takeUntil(this.plan_features_sub))
      .subscribe({
        next: (res: { features: FeatureType[] }) => {
          this.feature_list = res.features.sort((a, b) => (a.included > b.included ? -1 : 1));
          this.no_features = !res.features.length;

          this.feature_list.forEach((feature: FeatureType) => {
            if (feature.included) this.selectFeature(feature);
          });

          const cart_feature_ids: string[] = this.shopping_cart.shopping_cart_items
            .filter((item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE)
            .map((item: ShoppingCartItem) => String(item.id));
          const restore_ids: string[] = restore_feature_ids
            ? restore_feature_ids.map((id) => String(id))
            : cart_feature_ids;

          // Bring back any cart feature not returned by the API so the UI can render and re-select it.
          restore_ids
            .filter(
              (id: string) =>
                !this.feature_list.some((feature: FeatureType) => String(feature.id) === id)
            )
            .forEach((id: string) => {
              const cart_item = this.shopping_cart.shopping_cart_items.find(
                (item: ShoppingCartItem) => String(item.id) === id
              );
              if (cart_item) {
                this.feature_list.push({
                  id: Number(cart_item.id),
                  feature_id: Number(cart_item.id),
                  name: cart_item.name ?? cart_item.additional_info?.option ?? '',
                  name_override: cart_item.additional_info?.option ?? cart_item.name ?? '',
                  category: cart_item.additional_info?.category ?? '',
                  description: cart_item.description ?? '',
                  cost_to_currency:
                    cart_item.monthly_cost_to_currency ??
                    cart_item.cost_to_currency ??
                    cart_item.onetime_cost_to_currency ??
                    '',
                  included: false,
                  unchanged: Boolean(cart_item.additional_info?.unchanged),
                  admin: cart_item.additional_info?.admin
                } as FeatureType);
              }
            });

          this.feature_list.forEach((feature: FeatureType) => {
            if (
              !feature.included &&
              restore_ids.includes(String(feature.id)) &&
              !this.selected_feature_list.find((f: FeatureType) => f.id === feature.id)
            ) {
              const feature_in_cart = !!this.getItemById(feature.id);
              if (feature_in_cart) this.selected_feature_list.push(feature);
              else this.selectFeature(feature);
            }
          });

          this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.reduce(
            (acc: ShoppingCartItem[], item: ShoppingCartItem) => {
              if (item.type === CART_ITEM_TYPE_FEATURE) {
                const in_response = this.feature_list.some(
                  (feature: FeatureType) => feature.id == item.id
                );
                if (!in_response && restore_ids.includes(String(item.id))) return [...acc, item];
                if (!in_response) {
                  this.selected_feature_list = this.selected_feature_list.filter(
                    (feature: FeatureType) => feature.id != item.id
                  );
                  cart_updated = true;
                  return acc;
                }
              }
              return [...acc, item];
            },
            []
          );

          if (cart_updated) {
            this.shopping_items_price.shopping_cart_items =
              this.shopping_items_price.shopping_cart_items.filter((item: ShoppingCartItem) =>
                this.getItemById(item.id)
              );
            this.refresh_summary.next(true);
          }

          this.createPayloadString();
        },
        error: (err: HttpErrorResponse) => {}
      });
  }

  private confirmChangesModal(callback: () => void, admin_cart_items?: ShoppingCartItem[]): void {
    this.modal_svc.confirmationDialog(
      {
        title: this.translation_svc.getTranslatedModalLabels()[0],
        message: this.translation_svc.getTranslatedModalLabels()[1],
        confirm_button_text: this.translation_svc.translate('continue'),
        cancel_button_text: this.translation_svc.translate('cancel'),
        onConfirm: () => {
          this.shopping_svc
            .clearSpecifiedCartItems(
              calculateItemsToDelete(admin_cart_items ?? this.shopping_cart.shopping_cart_items),
              false
            )
            .subscribe({
              next: () => {
                this.revisiting = false;
                this.in_cart_device = null;
                callback();
                this.modal_dismissed = true;
                this.modal_svc.dismissAll();
              },
              error: () => {}
            });
        }
      },
      {
        size: 'dialog-centered',
        centered: true,
        backdrop: 'static'
      }
    );
  }

  private selectCarrier(device_item: DeviceItemData): void {
    if (this.selected_carrier_id == device_item.carrier.id) return;

    this.resetPlansAndFeatures();
    this.assignData(device_item);
  }

  private selectColor(device_details: DeviceItemDevice): void {
    if (this.selected_color_device_id === device_details.id) return;

    this.active_device = device_details;
    this.resetPlansAndFeatures();

    if (this.keep_plan) this.keepPlansAndFeatures();

    this.selected_device_id = this.active_device.id;
    this.selected_normalized_color = this.active_device.color;
    this.selected_color_device_id = device_details.id;
    this.is_display_one_time_cost = true;
    this.updateQueryParams();
    this.loadStorageList(this.active_device);
  }

  private selectStorage(device_details: DeviceItemDevice): void {
    if (
      this.selected_storage_size === device_details.size &&
      device_details.id === this.selected_storage_device_id
    )
      return;

    this.activity_state_svc.setSteps(this.progress_bar_config);

    if (
      !this.revisiting &&
      !this.shopping_svc.preselected_item &&
      this.selected_storage_device_id !== -1 &&
      device_details.id !== this.selected_storage_device_id
    )
      this.resetPlansAndFeatures();

    const is_same_storage: boolean = device_details.id === this.selected_storage_device_id;
    if (!this.keep_plan && !is_same_storage) this.resetPlanSelection(true);

    this.selected_storage_device_id = device_details.id;
    this.selected_storage_device_price.onetime = device_details.price.onetime;
    this.selected_storage_device_price.monthly = device_details.price.monthly;

    if (this.keep_plan) this.keepPlansAndFeatures();

    this.selected_storage_size = device_details.size;
    this.is_display_one_time_cost = true;

    this.loadNameOverrides(device_details, true);
  }

  private selectNameOverride(device_details: DeviceItemDevice, force_load: boolean = false): void {
    if (!force_load && this.selected_name_override_id === device_details.id) return;

    this.activity_state_svc.setSteps(this.progress_bar_config);

    const cart_device: ShoppingCartItem | undefined = this.getItemByType(CART_ITEM_TYPE_DEVICE);
    const preserve_cart_plan: boolean =
      this.revisiting &&
      !!this.getItemByType(CART_ITEM_TYPE_PLAN) &&
      (cart_device?.id == device_details.id || this.selected_device_id == device_details.id);

    if (!this.keep_plan && !this.admin_override_persist) {
      if (!preserve_cart_plan) this.resetPlanSelection();
      this.loadDevicePlanData(device_details.id);
    } else if (device_details.admin || this.admin_override_persist) {
      const cart_objects: InCartObjects = this.retrieveCartObjects(device_details);

      this.loadDevicePlanData(cart_objects.device_id ?? this.selected_device_id, () => {
        const features_to_restore: FeatureType[] =
          cart_objects.features ?? this.selected_feature_list;
        const restore_feature_ids: Array<string | number> = this.shopping_cart.shopping_cart_items
          .filter((item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE)
          .map((item: ShoppingCartItem) => item.id);
        const plan_id_to_load: number | undefined =
          cart_objects.plan_id && cart_objects.plan_id > 0
            ? cart_objects.plan_id
            : this.selected_plan_id && this.selected_plan_id > 0
              ? this.selected_plan_id
              : undefined;

        if (plan_id_to_load) {
          this.loadFeatureList(plan_id_to_load, restore_feature_ids);
          this.selected_plan_id = plan_id_to_load;
          this.selected_feature_list = features_to_restore.filter(
            (feature: FeatureType) => !!this.getItemById(feature.id)
          );
          features_to_restore
            .filter((feature: FeatureType) => !this.getItemById(feature.id))
            .forEach((feature: FeatureType) => this.selectFeature(feature));
        } else {
          this.feature_list = this.selected_feature_list = [];
          this.removeItems(CART_ITEM_TYPE_FEATURE);
          this.refresh_summary.next(true);
        }
      });
    }

    this.removeItems(CART_ITEM_TYPE_DEVICE);

    this.device_plan.deviceId = this.selected_name_override_id = device_details.id;
    this.is_display_one_time_cost = true;

    if (
      (this.show_options || this.name_override_list.length === 1) && // <--- we need this change
      this.shopping_cart.existing_asset
    ) {
      if (!this.device_selection_device_id && this.shopping_cart.existing_asset)
        this.device_selection_device_id = this.shopping_cart.existing_asset.device.id;

      let existing_asset: UserAsset = {
        device: {
          existing_asset_id:
            this.shopping_cart?.add_additional_line ||
            this.shopping_cart?.existing_asset?.device?.is_override
              ? (this.shopping_cart?.existing_asset?.device?.existing_asset_id ??
                this.shopping_cart?.existing_asset?.device?.id)
              : this.device_selection_device_id,
          id: device_details.id,
          manufacturer: device_details.manufacturer,
          model: device_details.model,
          color: device_details.color,
          type: device_details.product_category,
          imei: this.shopping_cart.existing_asset?.device.imei,
          imei2: this.shopping_cart.existing_asset?.device?.imei2,
          eid: this.shopping_cart.existing_asset?.device?.eid,
          meid: this.shopping_cart.existing_asset?.device?.meid,
          esn: this.shopping_cart.existing_asset?.device?.esn,
          msn: this.shopping_cart.existing_asset?.device?.msn,
          device_catalog_id: device_details.device_catalog_id,
          size: device_details.size,
          serial_number: this.shopping_cart.existing_asset?.device.serial_number,
          tech_specs: {
            sim_compatibility:
              this.shopping_cart.existing_asset?.device.tech_specs.sim_compatibility
          },
          image_url: device_details.image_url,
          normalized_color: device_details.original_color,
          is_override: true
        },
        asset_from_inventory: this.shopping_cart.existing_asset.asset_from_inventory,
        asset_selection_case: this.shopping_cart.existing_asset.asset_selection_case,
        device_identifier_search_value:
          this.shopping_cart.existing_asset?.device_identifier_search_value,
        multi_sim_type: this.shopping_cart.existing_asset?.multi_sim_type
      } as UserAsset;

      if (this.shopping_cart?.existing_asset?.line)
        existing_asset.line = this.shopping_cart.existing_asset?.line;

      if (
        this.shopping_cart?.existing_asset?.additional_lines &&
        this.shopping_cart.existing_asset?.additional_lines?.length > 0
      )
        existing_asset.additional_lines = this.shopping_cart.existing_asset?.additional_lines;

      if (
        this.shopping_cart?.existing_asset?.additional_devices &&
        this.shopping_cart.existing_asset?.additional_devices?.length > 0
      )
        existing_asset.additional_devices = this.shopping_cart.existing_asset?.additional_devices;

      this.shopping_cart.existing_asset = existing_asset;
    }

    this.generateShoppingItems(
      device_details,
      CART_ITEM_TYPE_DEVICE,
      true,
      this.devices_payload.categories[0].id
    );
  }

  private generateShoppingItems(
    new_cart_item: any,
    new_cart_type: string,
    refresh: boolean = false,
    category_id?: number
  ): void {
    const item_already_in_cart: boolean = !!this.getItemById(new_cart_item.id);

    if (
      item_already_in_cart &&
      !new_cart_item.included &&
      !new_cart_item.unchanged &&
      new_cart_type === CART_ITEM_TYPE_FEATURE
    ) {
      this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.filter(
        (item: ShoppingCartItem) => item.id != new_cart_item.id
      );
      this.shopping_items_price.shopping_cart_items =
        this.shopping_items_price.shopping_cart_items.filter(
          (item: ShoppingCartItem) => item.id != new_cart_item.id
        );
    } else {
      if (category_id) new_cart_item.category_id = category_id;

      this.shopping_cart.shopping_cart_items.push(
        createShoppingObject(
          new_cart_item,
          new_cart_type,
          this.keep_plan
            ? this.existing_image_url
            : this.selected_devices_by_carrier.carrier.image_url,
          this.is_bp_device
        )
      );
      this.shopping_items_price.shopping_cart_items.push(
        createShoppingPriceObject(new_cart_item, new_cart_type)
      );
    }

    this.shopping_cart.carrier = this.selected_devices_by_carrier.carrier;
    this.createPayloadString();
    this.refresh_summary.next(refresh);
  }

  private displayOldPlanAndFeatures(shopping_cart: ShoppingCartData): void {
    this.shopping_cart.keep_plan = this.shopping_items_price.keep_plan = this.keep_plan;

    const selected_cart_items: ShoppingCartItem[] = shopping_cart.shopping_cart_items.filter(
      (item: ShoppingCartItem) =>
        item.type === CART_ITEM_TYPE_DEVICE || item.type === CART_ITEM_TYPE_ACCESSORY
    )!;

    if (selected_cart_items.length > 0)
      selected_cart_items.forEach((item: ShoppingCartItem) => {
        if (!this.getItemById(item.id))
          this.generateShoppingItems(item, item.type, false, item.additional_info?.category_id);
      });

    if (this.keep_plan) {
      this.shopping_svc.preselected_item = true;

      const businessPlan = shopping_cart.existing_asset?.line?.business_plan;
      const linePlan = shopping_cart.existing_asset?.line?.plan;

      let old_plan_and_features: PlanType | LinePlan | undefined =
        businessPlan && 'full_description' in businessPlan ? (businessPlan as PlanType) : linePlan;

      this.existing_image_url = this.selected_devices_by_carrier.carrier.image_url;

      if (old_plan_and_features?.id) {
        const isPlanType = (plan: any): plan is PlanType => 'full_description' in plan;

        this.plan_list = [
          {
            name: isPlanType(old_plan_and_features)
              ? (old_plan_and_features.name_override ?? old_plan_and_features.name)
              : old_plan_and_features.name,

            id: old_plan_and_features?.id,
            carrier: { image_url: shopping_cart.existing_asset?.line?.carrier_image_url },
            cost_to_currency: old_plan_and_features?.cost_to_currency,
            full_description: old_plan_and_features?.full_description,
            description: old_plan_and_features?.description
          }
        ] as PlanType[];

        // There will only ever be one plan when we "keep plans and features"
        this.selectPlan(this.plan_list[0]);

        this.selected_plan_id = old_plan_and_features?.id as number;

        if (businessPlan?.features?.length)
          this.feature_list = businessPlan.features as FeatureType[];

        // For multiple features
        if (this.feature_list)
          for (const [index, feature_details] of this.feature_list.entries())
            this.selectFeature(feature_details);

        this.no_features = this.feature_list?.length ? false : true;
      } else this.no_plans = true;
    } else {
      // Add plan and not-included features
      const plan_and_features: ShoppingCartItem[] = shopping_cart.shopping_cart_items.filter(
        (item: ShoppingCartItem) =>
          item.type === CART_ITEM_TYPE_PLAN ||
          (item.type === CART_ITEM_TYPE_FEATURE && !item.additional_info?.included)
      )!;

      if (plan_and_features.length > 0)
        plan_and_features.forEach(
          (item: ShoppingCartItem) =>
            !this.getItemById(item.id) &&
            this.generateShoppingItems(item, item.type, false, item.additional_info?.category_id)
        );
    }
  }

  private createPayloadString(): void {
    this.shopping_cart.shopping_cart_items.map(
      (item) => (item.edit_link_payload = JSON.stringify(this.generateQueryParams()))
    );
  }

  private updateQueryParams(): void {
    this.router
      .navigate([], {
        relativeTo: this.activated_route,
        queryParams: this.generateQueryParams(),
        replaceUrl: true,
        queryParamsHandling: 'merge'
      })
      .then(() => {
        this.activity_state_svc.setSteps(this.progress_bar_config);
      });
  }

  private removeItems(type: string): void {
    this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.filter(
      (item: ShoppingCartItem) => item.type != type
    );
    this.shopping_items_price.shopping_cart_items =
      this.shopping_items_price.shopping_cart_items.filter(
        (item: ShoppingCartItem) => item.type != type
      );
  }

  private keepPlansAndFeatures(): void {
    if (this.plan_list && this.plan_list.length > 0) {
      this.selectPlan(this.plan_list[0]);
      this.feature_list.forEach((feature: FeatureType) => this.selectFeature(feature));
    }
    this.device_plan.deviceId = this.selected_device_id;
  }

  private resetPlanSelection(refresh: boolean = false): void {
    this.selected_plan_id = 0;
    this.feature_list = this.selected_feature_list = [];
    this.removeItems(CART_ITEM_TYPE_PLAN);
    this.removeItems(CART_ITEM_TYPE_FEATURE);
    if (refresh) this.refresh_summary.next(true);
  }

  private get isPlanSelected(): boolean {
    return (this.selected_plan_id ?? 0) > 0 || !!this.getItemByType(CART_ITEM_TYPE_PLAN);
  }

  private retrieveCartObjects(
    device_details?: DeviceItemDevice,
    existing_asset?: UserAsset
  ): InCartObjects {
    const device_item: ShoppingCartItem | undefined = this.shopping_cart?.shopping_cart_items.find(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_DEVICE
    );
    const plan_item: ShoppingCartItem | undefined = this.shopping_cart?.shopping_cart_items.find(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_PLAN
    );
    const feature_items: ShoppingCartItem[] = this.shopping_cart?.shopping_cart_items.filter(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE
    ) as ShoppingCartItem[];

    const resolved_device_id: number | string | undefined =
      [3].includes(this.current_activity_id) && this.revisiting && existing_asset?.device?.id
        ? existing_asset.device.id
        : device_details?.id && device_details.id !== device_item?.id
          ? device_details.id
          : device_item?.id;
    const device_id: number = resolved_device_id !== undefined ? Number(resolved_device_id) : -1;

    const plan_id: number =
      plan_item?.id !== undefined &&
      device_id !== -1 &&
      (this.selected_device_id === undefined || this.selected_device_id === device_id)
        ? Number(plan_item.id)
        : Number(this.selected_plan_id);

    return {
      device_id: device_id,
      plan_id: plan_id,
      features: feature_items as []
    };
  }

  private generateQueryParams(): StoreQueryParams {
    return {
      carrierId: this.selected_carrier_id,
      name: this.selected_device_name,
      filterConfigId: this.devices_payload.filters[0].id,
      isBpDevice: this.is_bp_device ?? undefined,
      filters:
        [3].includes(this.activity_state_svc.orderTypeId()) || this.is_bp_device
          ? this.url_filters_param
          : undefined,
      categories: JSON.stringify(this.devices_payload.categories),
      countryId: this.devices_payload.countryId,
      deviceId:
        this.in_cart_device?.id ??
        (this.getItemByType(CART_ITEM_TYPE_DEVICE)?.id as number) ??
        this.selected_device_id,
      number_of_plans: this.plan_list.length,
      number_of_features: this.feature_list.length
    };
  }

  private setCartValues(shopping_cart: ShoppingCartData): void {
    this.shopping_cart.order_for_person = shopping_cart.order_for_person;
    this.shopping_cart.fields = shopping_cart.fields;
    this.shopping_cart.device_identifier = shopping_cart.device_identifier;
    this.shopping_cart.order_sim = shopping_cart.order_sim;
    this.shopping_cart.new_sim = shopping_cart.new_sim;
    this.shopping_cart.esim_activation = shopping_cart.esim_activation;
    this.shopping_cart.new_eid = shopping_cart.new_eid;
    this.shopping_cart.device_include_admin_options = shopping_cart.device_include_admin_options;
    this.shopping_cart.accessories_include_admin_options =
      shopping_cart.accessories_include_admin_options;
    this.shopping_cart.order_type_settings = shopping_cart.order_type_settings;
    this.shopping_cart.quantity = shopping_cart.quantity;
    this.shopping_cart.add_additional_line = shopping_cart.add_additional_line;

    if (shopping_cart.orderTypeId === 3)
      this.shopping_cart.existing_asset = shopping_cart.existing_asset;
  }

  private resetPlansAndFeatures(): void {
    this.shopping_svc.preselected_item = false;
    this.selected_storage_size = '';
    this.name_override_list = [];
    this.selected_name_override_id = -1;
    this.admin_override_persist = false;

    if (!this.keep_plan) {
      this.plan_list = this.feature_list = this.selected_feature_list = [];
      this.selected_plan_id = 0;
      this.no_plans = this.no_features = false;
    }

    this.removeItems(CART_ITEM_TYPE_DEVICE);
    this.removeItems(CART_ITEM_TYPE_PLAN);
    this.removeItems(CART_ITEM_TYPE_FEATURE);

    if (
      this.shopping_cart.shopping_cart_items.filter(
        (cart_item: ShoppingCartItem) => cart_item.type === CART_ITEM_TYPE_ACCESSORY
      ).length > 0
    ) {
      this.is_display_one_time_cost = true;
      this.is_display_recurring_cost = false;
    } else this.is_display_one_time_cost = this.is_display_recurring_cost = false;

    this.device_plan.deviceId = null;
    this.plan_features_sub.next();
    this.refresh_summary.next(true);
  }
}
